import React, { useState } from "react";
import Container from "../components/common/Container";
import MetaTags from "../components/common/MetaTags";
import StaticHeaderImage from "../assets/img/common/static-pages-header.png";
import { HiMail, HiQuestionMarkCircle } from "react-icons/hi";
import { FaGithub, FaYoutube, FaLinkedin, FaTelegram, FaTwitter } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const socialLinks = [
  {
    name: "GitHub",
    icon: FaGithub,
    url: "https://github.com/Fundesci",
  },
  {
    name: "YouTube",
    icon: FaYoutube,
    url: "https://www.youtube.com/@fundesci",
  },
  {
    name: "LinkedIn",
    icon: FaLinkedin,
    url: "https://www.linkedin.com/company/fundesci",
  },
  {
    name: "Telegram",
    icon: FaTelegram,
    url: "https://t.me/fundesci",
  },
  {
    name: "X (Twitter)",
    icon: FaTwitter,
    url: "https://x.com/fundesci",
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    category: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // For now, we'll send an email directly
      // In production, this should go through an API endpoint
      const mailtoLink = `mailto:info@fundesci.com?subject=${encodeURIComponent(
        `[${formData.category || "General"}] Contact from ${formData.firstName} ${formData.lastName}`
      )}&body=${encodeURIComponent(
        `Name: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nCategory: ${formData.category}\n\nMessage:\n${formData.message}`
      )}`;
      
      window.location.href = mailtoLink;
      
      toast.success("Opening your email client...");
      
      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        category: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center pt-32">
      <MetaTags
        title="Contact Us"
        description="Get in touch with FunDeSci. Whether it's a quick coffee catch-up or a longer conversation, we love to hear from you."
        link="/contact"
      />

      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `url(${StaticHeaderImage})`,
        }}
        className="w-full flex flex-col justify-center items-center h-64 bg-[rgba(30,21,65,0.85)] bg-no-repeat bg-cover bg-blend-multiply bg-center"
      >
        <h1 className="text-4xl sm:text-5xl text-center abel-font font-bold text-white tracking-wider mb-4">
          Contact Us
        </h1>
        <p className="text-white/70 max-w-2xl text-center px-4">
          We'd love to hear from you. Get in touch with our team.
        </p>
      </section>

      {/* Contact Content */}
      <section className="w-full py-20 bg-gradient-to-b from-white to-whitesmoke">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Get in Touch
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-secondary abel-font mb-6">
                We're Here to Help
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether it's a quick coffee catch-up or a longer conversation, we love to hear about your experiences, adventures, and any new developments since we last spoke.
              </p>

              {/* Quick Links */}
              <div className="grid gap-4 mb-8">
                <Link
                  to="/FAQ"
                  className="flex items-center gap-4 p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <HiQuestionMarkCircle className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-secondary">Frequently Asked Questions</h3>
                    <p className="text-gray-500 text-sm">Find quick answers to common questions</p>
                  </div>
                </Link>
              </div>

              {/* Email */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-secondary mb-3">
                  Send a Message
                </h3>
                <a
                  href="mailto:info@fundesci.com"
                  className="flex items-center gap-3 text-primary hover:text-primary/80 transition-colors"
                >
                  <HiMail size={20} />
                  info@fundesci.com
                </a>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-lg font-semibold text-secondary mb-4">
                  Follow Us
                </h3>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-white/20 hover:border-primary/30 hover:shadow-md transition-all duration-300 text-gray-600 hover:text-primary"
                    >
                      <social.icon size={18} />
                      <span className="text-sm font-medium">{social.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Note */}
              <p className="mt-8 text-gray-500 text-sm bg-gray-50 p-4 rounded-lg">
                Please note that it may take 24 to 72 hours to review your request.
              </p>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-lg">
              <h3 className="text-2xl font-bold text-secondary abel-font mb-6">
                Send us a Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300"
                  >
                    <option value="">Select a category</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="General Feedback">General Feedback</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Press & Media">Press & Media</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-white/30 bg-white/50 backdrop-blur-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-primary to-[#7e7cff] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Contact;
